import { useMutation } from "@apollo/react-hooks";
import { useAsyncCallback } from 'react-use-async-callback';
import gql from "graphql-tag";
import { AsyncActionStatus } from '../../../shared/abstractStore';
import { QuestionnaireUpdateInput } from '../generated/globalTypes';
import { updateQuestionnaireMutation, updateQuestionnaireMutationVariables } from '../generated/updateQuestionnaireMutation';
import { questionnaireFieldsFragment } from '../models/Questionnaire';

/**
 * Get a callback to update a Questionnaire in the store.
 */
export function useUpdateQuestionnaireCallback(): [(id: string, model: QuestionnaireUpdateInput) => Promise<void>, AsyncActionStatus] {
    const [mutationAction, { error }] = useMutation<updateQuestionnaireMutation, updateQuestionnaireMutationVariables>(
        gql`
            mutation updateQuestionnaireMutation ($model: QuestionnaireUpdateInput!) {
                updateQuestionnaire(model: $model) {
                    ...questionnaireFields
                }
            }

            ${questionnaireFieldsFragment}
        `
    );

    // Wrap the mutation in a standard callback.
    const [action, { isExecuting, errors: actionErrors }] = useAsyncCallback(async (id: string, model: QuestionnaireUpdateInput) => {
        const result = await mutationAction({ variables: { model: { ...model, id: id } } });
        if (result.errors && result.errors.length) {
            throw result.errors;
        }
    }, [mutationAction]);

    return [action, { isExecuting: isExecuting, errors: error ? error.message : actionErrors }];
}
